'use client'
import { useState, useRef, useCallback } from 'react'

export interface UseHeaderUIReturn {
    menuStates: {
        user: boolean
        language: boolean
        currency: boolean
    }
    menuAnchors: {
        user: HTMLElement | null
        language: HTMLElement | null
        currency: HTMLElement | null
    }
    modals: {
        signIn: boolean
    }
    onMenuOpen: (menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: (menu: 'user' | 'language' | 'currency') => void
    onModalOpen: (modal: 'signIn') => void
    onModalClose: (modal: 'signIn') => void
}

/** Hook for header UI state management (menus and modals) */
export const useHeaderUI = (): UseHeaderUIReturn => {
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const languageMenuAnchorRef = useRef<HTMLElement | null>(null)
    const currencyMenuAnchorRef = useRef<HTMLElement | null>(null)

    const [menuStates, setMenuStates] = useState<{
        user: boolean
        language: boolean
        currency: boolean
    }>({ user: false, language: false, currency: false })

    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })

    const onMenuOpen = useCallback((menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => {
        if (menu === 'user') {
            userMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = event.currentTarget
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = event.currentTarget
        }
        setMenuStates(prev => ({ ...prev, [menu]: true }))
    }, [])

    const onMenuClose = useCallback((menu: 'user' | 'language' | 'currency') => {
        if (menu === 'user') {
            userMenuAnchorRef.current = null
        } else if (menu === 'language') {
            languageMenuAnchorRef.current = null
        } else if (menu === 'currency') {
            currencyMenuAnchorRef.current = null
        }
        setMenuStates(prev => ({ ...prev, [menu]: false }))
    }, [])

    const onModalOpen = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: true }))
    }, [])

    const onModalClose = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: false }))
    }, [])

    return {
        menuStates,
        menuAnchors: {
            user: userMenuAnchorRef.current,
            language: languageMenuAnchorRef.current,
            currency: currencyMenuAnchorRef.current
        },
        modals,
        onMenuOpen,
        onMenuClose,
        onModalOpen,
        onModalClose
    }
}
